// src/lib/aiDetector.ts

export function analyzeCodeForAI(code: string, description: string): { aiScore: number; breakdown: string[] } {
  let score = 30; // higher baseline for modern LLM capabilities
  const breakdown: string[] = [];

  const lowerCode = code.toLowerCase();
  const lowerDesc = (description || '').toLowerCase();
  const lines = code.split('\n').map(l => l.trim()).filter(Boolean);

  // 1. Conversational & Prompt Artifact Detection
  const aiPhrases = [
    'here is the', 'sure, here', 'certainly', 'as an ai', 
    'note that', 'feel free to', 'hope this helps', 'example usage',
    'todo: implement', 'replace with your', 'lets break down',
    'this script', 'this function', 'in order to'
  ];
  
  let fillerMatches = 0;
  aiPhrases.forEach(phrase => {
    if (lowerCode.includes(phrase) || lowerDesc.includes(phrase)) {
      fillerMatches++;
    }
  });

  if (fillerMatches > 0) {
    const penalty = fillerMatches * 20;
    score += penalty;
    breakdown.push(`Detected conversational/boilerplate AI artifacts (+${penalty}%)`);
  }

  // 2. Canonical Textbook Algorithm Fingerprinting
  // AI models output identical textbook structures for common utilities (chunk, debounce, deepClone, sleep)
  const isTextbookChunker = /reduce\(\s*\(acc,\s*_,\s*i\)\s*=>\s*\(i\s*%\s*size\s*\?\s*acc\s*:\s*\[\s*\.\.\.acc/.test(code);
  const isTextbookSleep = /new\s+promise\s*\(\s*resolve\s*=>\s*settimeout\s*\(\s*resolve,\s*ms\s*\)\s*\)/i.test(code);
  const isTextbookDebounce = /clearTimeout\([^)]+\);\s*timeout\s*=\s*setTimeout/i.test(code);

  if (isTextbookChunker || isTextbookSleep || isTextbookDebounce) {
    score += 35;
    breakdown.push(`Canonical textbook RLHF algorithm fingerprint detected (+35% AI signature)`);
  }

  // 3. The "Uncanny Valley" of Over-Optimization (Detecting Evasion Attempts)
  // If code has zero comments, is ultra-short (< 5 lines), but crams advanced functional chaining and ternary nesting, 
  // it is almost certainly an LLM trying to simulate "human expert shorthand".
  const hasZeroComments = lines.every(l => !l.startsWith('//') && !l.startsWith('#') && !l.startsWith('/*'));
  const hasComplexChaining = (code.match(/\.(reduce|map|filter|flat|slice)\(/g) || []).length >= 2;
  const hasTernaryNesting = (code.match(/\?/g) || []).length >= 2;

  if (hasZeroComments && lines.length <= 4 && (hasComplexChaining || hasTernaryNesting)) {
    score += 30;
    breakdown.push(`Adversarial evasion pattern: Over-optimized functional one-liner with zero human artifacts (+30%)`);
  }

  // 4. Lexical Verbosity & Identifier Profiling
  const identifiers = code.match(/[a-zA-Z_][a-zA-Z0-9_]{3,}/g) || [];
  const avgIdentifierLength = identifiers.length > 0 
    ? identifiers.reduce((sum, id) => sum + id.length, 0) / identifiers.length 
    : 0;

  if (avgIdentifierLength > 9.5) {
    score += 15;
    breakdown.push(`Elevated identifier length consistency typical of LLM generation (+15%)`);
  }

  // 5. Structural Entropy & Pragmatic "Human Messiness" Bonus
  // Real humans writing quick snippets often include a local variable, an explicit assignment, or a standard loop.
  const hasProceduralLoop = /\b(for|while)\b/.test(code);
  const hasIntermediateAssignment = /(let|const|var)\s+[a-zA-Z0-9_]+\s*=\s*[^;]+;[\s\S]*?(let|const|var)\s+/.test(code);

  if (hasProceduralLoop || hasIntermediateAssignment) {
    score -= 20;
    breakdown.push(`Pragmatic procedural structure & intermediate state (-20% human craftsmanship)`);
  }

  // Clamp final score between 5% and 95%
  const finalScore = Math.max(5, Math.min(95, score));
  return { aiScore: finalScore, breakdown };
}